import type { WatchQueryFetchPolicy } from "@apollo/client";
import {
  ColumnPinningState,
  createColumnHelper,
  getCoreRowModel,
  getExpandedRowModel,
  TableOptions,
  useReactTable,
} from "@tanstack/react-table";
import { Text } from "@chakra-ui/react";

import { DocumentType, getFragmentData, gql } from "__generated__/gql";
import ChakraReactTable from "components/Shared/ChakraReactTable";
import {
  useChakraReactTableProps,
  usePrepApolloData,
} from "components/Shared/ChakraReactTable/utils";
import { useQuery } from "utils/apollo/hooks";
import { ADMIN_CONTEXT } from "utils/apollo/hooks/admin";
import { addActiveBrandIdToContext } from "utils/apollo/hooks/brand";
import { INVALID_UUID } from "utils/misc";
import usePagination, {
  usePaginationReset,
} from "components/Shared/ChakraReactTable/usePagination";
import useGQLSorting, {
  GQLOrderByEnumsById,
} from "components/Shared/ChakraReactTable/useGQLSorting";
import TableControlsFooter from "components/Shared/ChakraReactTable/TableControlsFooter";
import NumResultsLabel from "components/Shared/ChakraReactTable/NumResultsLabel";
import TableControls from "components/Shared/ChakraReactTable/TableControls";
import PaginationControls from "components/Shared/ChakraReactTable/PaginationControls";
import { CONTENT_FULL_HEIGHT } from "components/Layout/constants";
import useStickyHeader from "components/Shared/ChakraReactTable/useStickyHeader";
import ScrollContainer from "components/Shared/ChakraReactTable/ScrollContainer";
import {
  OrderingPlatformFilter,
  OrderingPlatformsOrderBy,
} from "__generated__/gql/graphql";
import useFixedLayout from "components/Shared/ChakraReactTable/useFixedLayout";
import useSelectableRow from "components/Shared/ChakraReactTable/useSelectableRow";
import { useCurrentBrandId } from "utils/currentUserHooks";
import { formatExternalDomainCell } from "components/Shared/ChakraReactTable/formatters";
import { NA } from "components/Shared/EmptyPlaceholder";
import TagList from "components/Shared/TagList";
import OrderingPlatformTypeBadge from "./OrderingPlatformTypeBadge";
import { BaseDistributionCenterName } from "./DistributionCenters/DistributionCenterName";

export const fragment = gql(/* GraphQL */ `
  fragment OrderingPlatformsDirectoryTableRow on OrderingPlatform {
    id
    name
    orderingPlatformType
    website
    description
    directRetailerId
    directRetailer {
      id
      ...RetailerNameWithFlagIconsFragment
    }
    isParent
    parentId
    parent {
      id
      name
    }
    forBrandId
    distributionCenters(
      filter: { name: { isNull: false }, isArchived: { equalTo: false } }
      orderBy: NAME_ASC
      first: 3
    ) {
      totalCount
      nodes {
        id
        ...DistributionCenterBaseNameFragment
      }
    }
    ...CustomerIsCustomBadge
  }
`);

const query = gql(/* GraphQL */ `
  query OrderingPlatformsDirectoryTable(
    $isAdmin: Boolean = false
    $brandId: UUID!
    $filter: OrderingPlatformFilter
    $orderBy: [OrderingPlatformsOrderBy!] = [NAME_ASC]
    $limit: Int = 10
    $offset: Int = 0
  ) {
    orderingPlatformsForBrand(
      brandId: $brandId
      filter: $filter
      first: $limit
      offset: $offset
      orderBy: $orderBy
    ) @skip(if: $isAdmin) {
      totalCount
      nodes {
        id
        ...OrderingPlatformsDirectoryTableRow
      }
    }
    orderingPlatforms(
      filter: $filter
      first: $limit
      offset: $offset
      orderBy: $orderBy
    ) @include(if: $isAdmin) {
      totalCount
      nodes {
        id
        ...OrderingPlatformsDirectoryTableRow
      }
    }
  }
`);

export type Datum = DocumentType<typeof fragment>;

const col = createColumnHelper<Datum>();

export const DEFAULT_COLUMNS = [
  col.accessor("name", {
    header: "Customer Name",
    cell: (cell) => <Text>{cell.getValue()}</Text>,
    size: 250,
  }),
  col.accessor("orderingPlatformType", {
    header: "Type",
    cell: (cell) => (
      <OrderingPlatformTypeBadge
        orderingPlatformType={cell.getValue()}
        isTruncated
        maxW="100%"
        fontSize=".8em"
        lineHeight={1.5}
      />
    ),
    size: 120,
    meta: { props: { textAlign: "center" }, cellProps: { isTruncated: true } },
  }),
  col.accessor("website", {
    header: "Website",
    cell: formatExternalDomainCell,
    size: 200,
    meta: { cellProps: { isTruncated: true } },
  }),
  col.display({
    id: "distributionCenters",
    header: "Distribution Centers",
    cell: (cell) => {
      const { totalCount, nodes: dcs } = cell.row.original.distributionCenters;
      if (!dcs?.length) return <NA />;
      return (
        <TagList
          items={dcs}
          numTotal={totalCount}
          getKey={(dc) => dc.id}
          getLabel={(dc) => (
            <BaseDistributionCenterName distributionCenter={dc} />
          )}
        />
      );
    },
    size: 375,
  }),
];

export const DEFAULT_PINNED_COLUMNS: ColumnPinningState = { left: ["name"] };

export const orderByEnumsById: GQLOrderByEnumsById<OrderingPlatformsOrderBy> = {
  name: [OrderingPlatformsOrderBy.NAME_ASC, OrderingPlatformsOrderBy.NAME_DESC],
};

export type OrderingPlatformsDirectoryTableProps = Partial<
  Pick<TableOptions<Datum>, "columns" | "meta">
> & {
  filter?: OrderingPlatformFilter;
  isAdmin?: boolean;
  nextFetchPolicy?: WatchQueryFetchPolicy;
  onSelectedChange: (id: UUID) => void;
  pinnedColumns?: ColumnPinningState;
  selected?: UUID | null;
};

export default function OrderingPlatformsDirectoryTable({
  columns = DEFAULT_COLUMNS,
  filter,
  isAdmin = false,
  meta,
  nextFetchPolicy,
  onSelectedChange,
  pinnedColumns: columnPinning = DEFAULT_PINNED_COLUMNS,
  selected,
}: OrderingPlatformsDirectoryTableProps) {
  const {
    pagination,
    onPaginationChange,
    limitOffset,
    getPageCount,
    setPageIndex,
  } = usePagination();

  const { sorting, onSortingChange, orderBy } = useGQLSorting(
    orderByEnumsById,
    [{ id: "name", desc: false }],
  );

  const brandId = useCurrentBrandId();

  const isPendingFilterReset = usePaginationReset(
    filter,
    setPageIndex,
    pagination.pageIndex,
  );

  const isPendingSortingReset = usePaginationReset(
    sorting,
    setPageIndex,
    pagination.pageIndex,
  );

  const {
    data: rawData,
    loading: isLoading,
    refetch,
  } = useQuery(query, {
    nextFetchPolicy,
    context: isAdmin ? ADMIN_CONTEXT : addActiveBrandIdToContext(brandId),
    skip:
      !(isAdmin || brandId) || isPendingFilterReset || isPendingSortingReset,
    variables: {
      isAdmin,
      brandId: isAdmin ? INVALID_UUID : brandId || INVALID_UUID,
      filter,
      orderBy,
      ...limitOffset,
    },
  });

  const { nodes, totalCount } =
    rawData?.orderingPlatformsForBrand || rawData?.orderingPlatforms || {};

  const tableInstance = useReactTable({
    columns,
    data: usePrepApolloData(getFragmentData(fragment, nodes)),
    meta,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    onPaginationChange,
    onSortingChange,
    manualPagination: true,
    manualSorting: true,
    enableSorting: true,
    state: { columnPinning, pagination, sorting },
    pageCount: getPageCount(totalCount),
  });

  const tableProps = useChakraReactTableProps<Datum>([
    useStickyHeader(),
    useFixedLayout(tableInstance),
    useSelectableRow({ selected, onSelectedChange }),
    {
      tableProps: { fontSize: "sm" },
      tableBodyProps: { sx: { "> tr > td": { py: 2 } } },
    },
  ]);

  return (
    <>
      <ScrollContainer
        minHeight="15rem"
        maxHeight={`calc(${CONTENT_FULL_HEIGHT} - 15.5rem)`}
        borderWidth={1}
        borderTopRadius=".25rem"
      >
        <ChakraReactTable
          tableInstance={tableInstance}
          {...tableProps}
          isLoading={isLoading || typeof window === "undefined"}
          noRowsPlaceholder={<Text>No customers found.</Text>}
        />
      </ScrollContainer>
      <TableControlsFooter
        py={4}
        borderTopWidth={1}
        leftContent={
          <NumResultsLabel
            {...{ totalCount, isLoading }}
            resultTypeLabel="customers"
          />
        }
        rightContent={<TableControls {...{ isLoading }} onRefresh={refetch} />}
      >
        <PaginationControls {...{ tableInstance, isLoading }} />
      </TableControlsFooter>
    </>
  );
}
